/**
 * The vocabularies: what a mayor may say to a district, and what a governor may.
 *
 * `CommandQueue` decides what order commands are applied in. This file decides
 * what a command may *say* — and that is a contract in the strong sense. A
 * client issues these, a server replays them a season later on a machine that
 * never saw the session, and the two have to mean exactly the same thing by
 * them. So the vocabulary is small, total, and boring, and it grows only when
 * somebody genuinely acquires a new power (design document §18).
 *
 * Today there are nine verbs: seven a mayor may say, two a governor may.
 *
 * ## Two actors, one queue
 *
 * `Command.actor` was always the first tie-break, and until now there was only
 * ever one actor in a world (`LOCAL_MAYOR`). `FORCE_PLACE` is the first verb
 * issued by somebody else, so the tie-break finally does something: a mayor's
 * `PAVE` and a governor's `FORCE_PLACE` stamped for the same tick apply in
 * actor-string order, `'governor'` before `'mayor'`, on every peer. Nothing had
 * to be added for that; it is what `compareCommands` was written for.
 *
 * ## PAVE is per cell (ADR-004, answering ADR-003 §8 Q6)
 *
 *     PAVE { x, y }   — advance the cell at (x, y) one construction step
 *
 * The alternative was `PAVE_STROKE(from, to, brush)`, with the simulation
 * rasterising the line. Per cell was chosen for three reasons:
 *
 *   - **It is faithful.** The log records the cells that changed, not a guess at
 *     what the hand meant. A replay is the season that happened.
 *   - **It does not decide the rasterisation question.** A stroke verb would
 *     have baked "the simulation draws the line" into the protocol. With a cell
 *     verb, a client that later interpolates between mouse positions emits more
 *     commands and nothing else changes — no new verb, no new log format, no
 *     re-recorded golden masters. The reverse move is not available.
 *   - **A rejected command reverts one cell by one step**, which is the smallest
 *     visible correction the scheme can produce. That matters for the Phase 4
 *     question of whether terrain may visibly revert (ADR-003 §8, Q6's rider).
 *
 * What it does *not* fix is that the input reaching the client is still ambient:
 * a brush applies once per `mousemove`, so a fast drag still leaves gaps. That
 * is now a client-side gap with a client-side fix, which is the point of
 * choosing the verb that keeps it one.
 */

import { Command, ScheduleRejection, ScheduleResult } from './commands';

/**
 * Verbs a mayor may address to a district.
 *
 * `PAVE` changes the ground. Two change nothing on the map at all, and they are
 * the first verbs in this vocabulary that are addressed to *somebody else's*
 * property — the two halves of §10's labour strike:
 *
 *     withdraw_staffing(facility_id) / restore_staffing
 *
 * §18 spells them exactly that asymmetrically, and this list keeps the asymmetry
 * rather than tidying it. Withdrawing is a decision about one facility, taken
 * against one contract, and the Chronicle should say which. Going back to work is
 * a decision about *working*, and a mayor who ends a strike ends the strike.
 *
 * ## The last three are §10's mercy builds, and they are not like the others
 *
 * Every verb above this line moves a number somebody wants moved: pavement is a
 * parking space, a strike is leverage. `COVENANT`, `HEDGEROW` and `UNDERCROSS`
 * cost the treasury money and yield the district nothing, ever — no revenue, no
 * uptime, no score. §10 is explicit that this is the point:
 *
 * > None of these pay. That is the point. The site's argument is restraint under
 * > uncertainty, not restraint because it's profitable — so the game prices mercy
 * > honestly and lets it be chosen or not.
 *
 * They are three verbs rather than one `MERCY { build, x, y }` for the reason
 * `PAVE` is per cell: a verb should name an act, and these are three different
 * acts at three different prices with three different irreversibilities. §18
 * already names one of them on its own (`covenant_land(cells)`), which settles the
 * question — a protocol that spelled a governor's condemnation against
 * `MERCY.build == 'covenant'` would be a protocol that had bundled the one thing
 * anybody above needs to read.
 */
export const MayorVerb = {
  /** Advance one cell one construction step: natural → surveyed → … → painted. */
  PAVE: 'PAVE',
  /**
   * Order this district's families to stay home from one federal facility.
   *
   * §10: *"public, measurable, the labor strike. Output stops; wages stop; the
   * governor notices."* Nobody is fired and nobody is conscripted — the roster is
   * untouched, the staff simply do not go, and every consequence of that is
   * arithmetic somebody else has to look at.
   */
  WITHDRAW_STAFFING: 'WITHDRAW_STAFFING',
  /** End every strike in the district. Takes no argument; §18 does not give it one. */
  RESTORE_STAFFING: 'RESTORE_STAFFING',
  /**
   * Deed one cell wild, permanently.
   *
   * §18's `covenant_land(cells)`, one cell at a time. *"Cells locked NATURAL
   * forever; bushes persist."* The mayor's own brush stops working on it, and so
   * does the house spawner — which is the whole price, and it is paid in ground
   * rather than in dollars. Irreversible by the mayor: there is no verb in this
   * vocabulary that lifts one.
   *
   * Not irreversible by a *governor*. A `FORCE_PLACE` seals a covenanted cell like
   * any other, and §10 says so — *"irreversible except by condemnation
   * (Charter-gated), which is the most public aggression a governor can commit"*.
   * The Charter that would gate it does not exist yet; what exists is the count.
   */
  COVENANT: 'COVENANT',
  /**
   * Plant a bush on one unsealed cell, to come in over the next several cycles.
   *
   * §10's *"Hedgerow | Low, slow | Plant bushes on unsealed land; new food sources
   * grow over seasons. Paved land can never be unsealed — nurture is possible,
   * undoing is not."* The only verb in this vocabulary that adds something living
   * to a district, and the only one whose effect arrives long after the money left.
   */
  HEDGEROW: 'HEDGEROW',
  /**
   * Build a wildlife undercrossing beneath one paved cell.
   *
   * §10's *"Wildlife undercrossing | High, one-time | Ants path beneath a road
   * segment; cars can't kill what crosses there."* Beneath a road, so the cell has
   * to be paved: this is the one verb a mayor can only issue on ground they have
   * already ruined.
   *
   * Nothing in the protocol says what crosses there, and nothing needs to. The verb
   * names a cell under a road; what the simulation does with it is the simulation's,
   * and Law 7 is why it is spelled that way round.
   */
  UNDERCROSS: 'UNDERCROSS',
  /**
   * Build a two-cell interchange on the district's own road.
   *
   * §5's *"District maps gain **interchanges**: edge structures (2 cells) where
   * national freight enters and leaves the map."* The one verb in this vocabulary
   * that connects the district to something outside it: with two of them and a road
   * between, national freight crosses this map, and §12's promise that *"a
   * well-placed interchange makes the district a corridor (tolls, but trucks)"* is
   * a decision a mayor takes with a brush.
   *
   * The axis is an argument because a two-cell structure has an orientation and the
   * design does not give it one. A client may derive it from the road under the
   * cursor; the log has to say which way the ramp lay, or a replay would build a
   * different district.
   *
   * §18 does not list it. The mayor's verb list there is about *negotiating* — offer
   * a site, prepare it, object, petition — and this is ordinary construction that
   * happens to have a counterparty. `prepare_site(cells, works)` is where §7 puts an
   * *interchange spur* built for somebody else's facility; this is the interchange
   * itself, built for the district.
   */
  INTERCHANGE: 'INTERCHANGE',
} as const;

export type MayorVerb = typeof MayorVerb[keyof typeof MayorVerb];

/**
 * Verbs a governor may address to a district.
 *
 * §18 gives a governor twenty-one of these, and the two here are the two that
 * need no counterparty. Every other way a polity reaches a district runs through
 * the Siting Protocol (§7) or the mail: a notice, offers from mayors,
 * objections, counters, an award. All of that is a conversation, and a
 * conversation needs somebody at the other end of it.
 *
 * `FORCE_PLACE` is what the design calls the desperation move — *"the governor
 * places the rectangle anywhere, immediately, paying only statutory
 * compensation"* — and it is therefore the one verb in the list that a district
 * with no polity above it can be on the receiving end of. Law 6 asks that force
 * be cheaper now and more expensive later; every one of the later costs
 * (readiness, local hire, a mayor who volunteers the next site) belongs to a
 * mechanic that does not exist yet, which means this build ships the cheap half
 * of the bargain on its own. `docs/facilities.md` says so in as many words
 * rather than leaving the arithmetic to look balanced.
 *
 * `SET_TITHE` is the other one, and it is a different kind of act: it takes no
 * land and touches no cell. §8 calls it *the extraction dial* and gives it one
 * number per district per season, which is the whole of its interface.
 */
export const GovernorVerb = {
  /**
   * Put a facility's footprint on a district's map, whatever is under it.
   *
   * §18 spells it `force_place(district, rect)`. There is one district here, so
   * the district is implicit; the rect is the origin plus the facility's own
   * footprint, because a facility's size is a property of the facility and not
   * something an issuer may choose.
   */
  FORCE_PLACE: 'FORCE_PLACE',
  /**
   * Set the share of a district's tax revenue remitted upward each season.
   *
   * §18's `set_tithe(district, pct)`. §8 gives it one job — *"One number per
   * district per season: a percentage (Charter-capped, e.g. 0–40%) of district tax
   * revenue, remitted upward"* — and calls the mechanism it drives *"the entire
   * antification pump"*.
   *
   * **The rate may be negative, and a negative tithe is §18's `grant`.** §9's
   * Long Summer says so in as many words: *"Tithes to zero, then a season of
   * negative tithe — grants flow down."* One dial rather than two verbs, because a
   * share of measured tax revenue is a quantity Law 1 can defend and an arbitrary
   * transfer is not: a grant expressed this way is still a sum over something the
   * simulation counted.
   *
   * A verb rather than a dial, unlike the power allocation and the rainfall, and
   * the reason is what it is levied *against*. An allocation is re-decided from
   * scratch every cycle, so a district restored from a snapshot re-derives its
   * brownouts; a tithe is charged once, against a whole season's revenue, at the
   * moment the season closes. Which tick the rate changed on therefore changes the
   * money — and a scalar set by method call between ticks is not in the command
   * log, so a replayed season would come out different. Same argument
   * `SurfaceSim.facilities` makes about a slab, arriving at the same answer for
   * the first quantity that is not a map.
   */
  SET_TITHE: 'SET_TITHE',
} as const;

export type GovernorVerb = typeof GovernorVerb[keyof typeof GovernorVerb];

/** The actor a single-player session issues under. */
export const LOCAL_MAYOR = 'mayor';

/**
 * The actor a scripted governor issues under.
 *
 * A stand-in for a polity, exactly as `LOCAL_MAYOR` is a stand-in for an
 * account. It sorts before `'mayor'`, which is the arbitrary half of a total
 * order and is fine — what matters is that every peer sorts it the same way.
 */
export const LOCAL_GOVERNOR = 'governor';

/**
 * The facility types a `FORCE_PLACE` may name.
 *
 * The *names* are protocol because two machines have to agree what
 * `'data-center'` means before either can replay a command that says it. The
 * *numbers* — footprint, draw, staffing — are world parameters and live in
 * `@antification/core`, for the same reason a snapshot carries no configuration:
 * a season is settled under parameters rather than carrying them.
 *
 * §6 specifies six types. One is here, because one is built: a name in this list
 * that no simulation can site is a promise the protocol cannot keep.
 */
export const FacilityTypeName = {
  DATA_CENTER: 'data-center',
} as const;

export type FacilityTypeName = typeof FacilityTypeName[keyof typeof FacilityTypeName];

/** The facility type names, in a fixed order. Iteration order is never incidental. */
export const FACILITY_TYPE_NAMES = Object.freeze([
  FacilityTypeName.DATA_CENTER,
] as const);

/**
 * `INTERCHANGE` arguments: the first cell, and which way the second one lies.
 *
 * The structure is `(x, y)` plus `(x + 1, y)` when the axis is `'h'` and
 * `(x, y + 1)` when it is `'v'`. Both cells have to be on the map — a structure
 * that straddled the seam of a toroidal grid would be one no renderer, tooltip or
 * footprint check in this build could describe — and both have to be road.
 *
 * The axis is spelled rather than inferred for the reason `PAVE` is per cell
 * (above): a verb should record what was built and not a guess at what the hand
 * meant. A client that picks the axis from the road under the cursor is making a
 * client's decision, and a replay of the log builds the interchange that was
 * actually built.
 */
export interface InterchangeArgs {
  x: number;
  y: number;
  /** `'h'`: the cell to the east. `'v'`: the cell to the south. */
  axis: 'h' | 'v';
}

/** The two axes, in a fixed order. Iteration order is never incidental. */
export const INTERCHANGE_AXES = Object.freeze(['h', 'v'] as const);

/**
 * A cell, named by a verb.
 *
 * Four of the mayor's six verbs take exactly this and nothing else, so there is
 * one interface and one reader for all of them. `PaveArgs` is an alias kept for
 * the name's sake: `readPaveArgs(command.args)` reads better at the call site than
 * `readCellArgs`, and the three mercy verbs read better the other way round.
 */
export interface CellArgs {
  x: number;
  y: number;
}

/** `PAVE` arguments. */
export type PaveArgs = CellArgs;

/** `FORCE_PLACE` arguments: what to site, and the north-west corner of it. */
export interface ForcePlaceArgs {
  facility: FacilityTypeName;
  x: number;
  y: number;
}

/**
 * `WITHDRAW_STAFFING` arguments: which facility the staff stop going to.
 *
 * A facility *id*, not a type — a district may host two data centres and a mayor
 * striking one of them is a different sentence from a mayor striking both. Ids
 * are assigned by the simulation in siting order, so a replayed log names the
 * same slab it named the first time.
 */
export interface WithdrawStaffingArgs {
  facility: number;
}

/**
 * `SET_TITHE` arguments: the share of tax revenue remitted upward.
 *
 * A fraction rather than §18's *pct*, because every other ratio in this
 * simulation is one (`TAX_RATE`, `DIVIDEND_RATE`, `INTEREST_RATE`) and a
 * vocabulary with two conventions for the same kind of number is a vocabulary
 * somebody will get wrong at the worst moment.
 *
 * Finite, and that is the only thing checked here. Whether the rate is inside the
 * Charter's cap is a question about the world the command applies in, and the
 * Charter is Phase 7's — so the simulation refuses an out-of-range rate the way it
 * refuses a `PAVE` on wet concrete, with `applied: false`.
 */
export interface TitheArgs {
  /** Fraction of the season's district tax revenue. Negative is a grant. */
  rate: number;
}

/**
 * Read a cell out of a command's arguments, or null if it is not there.
 *
 * Args arrive as `Record<string, unknown>` because they may have come off a
 * wire or out of a replay file, and a verb has to survive being handed
 * something that is not what it expected. Integers only: a fractional cell
 * index would be a different cell on a machine that rounds differently.
 */
export function readCellArgs(args: Record<string, unknown> | undefined): CellArgs | null {
  if (!args) return null;
  const { x, y } = args as { x?: unknown; y?: unknown };
  if (!Number.isInteger(x) || !Number.isInteger(y)) return null;
  return { x: x as number, y: y as number };
}

/** Read `PAVE` arguments out of a command. See `readCellArgs`. */
export function readPaveArgs(args: Record<string, unknown> | undefined): PaveArgs | null {
  return readCellArgs(args);
}

/**
 * Read `FORCE_PLACE` arguments out of a command, or null if they are not there.
 *
 * The facility name is checked against the list rather than cast to it. A
 * replayed log is the one input to this system that nobody has validated, and a
 * command naming a facility this build cannot site has to be refused rather than
 * silently sited as something else.
 */
export function readForcePlaceArgs(
  args: Record<string, unknown> | undefined
): ForcePlaceArgs | null {
  if (!args) return null;
  const { facility, x, y } = args as { facility?: unknown; x?: unknown; y?: unknown };
  if (!Number.isInteger(x) || !Number.isInteger(y)) return null;
  if (typeof facility !== 'string') return null;
  if (!FACILITY_TYPE_NAMES.includes(facility as FacilityTypeName)) return null;
  return { facility: facility as FacilityTypeName, x: x as number, y: y as number };
}

/**
 * Read `WITHDRAW_STAFFING` arguments out of a command, or null if they are not
 * there.
 *
 * A positive integer, because ids start at 1. Whether a facility with that id is
 * standing is a question about the map *at the tick the command applies*, which
 * is not a question this function is in a position to ask — the same distinction
 * `SurfaceSim.validate` draws for `FORCE_PLACE`'s footprint.
 */
export function readWithdrawStaffingArgs(
  args: Record<string, unknown> | undefined
): WithdrawStaffingArgs | null {
  if (!args) return null;
  const { facility } = args as { facility?: unknown };
  if (!Number.isInteger(facility) || (facility as number) < 1) return null;
  return { facility: facility as number };
}

/**
 * Read `SET_TITHE` arguments out of a command, or null if they are not there.
 *
 * A finite number, and not an integer — this is the first verb in the vocabulary
 * whose argument is a ratio. `Number.isFinite` rejects the two values that would
 * otherwise poison a whole district's arithmetic silently: a `NaN` rate makes
 * every levy `NaN`, and an infinite one empties a treasury in one season.
 */
export function readTitheArgs(
  args: Record<string, unknown> | undefined
): TitheArgs | null {
  if (!args) return null;
  const { rate } = args as { rate?: unknown };
  if (typeof rate !== 'number' || !Number.isFinite(rate)) return null;
  return { rate };
}

/**
 * Read `INTERCHANGE` arguments out of a command, or null if they are not there.
 *
 * The axis is checked against the list rather than cast to it, on the same terms
 * as `FORCE_PLACE`'s facility name: a replayed log is the one input to this system
 * nobody has validated, and a command with a garbled axis has to be refused rather
 * than silently built the other way round.
 */
export function readInterchangeArgs(
  args: Record<string, unknown> | undefined
): InterchangeArgs | null {
  if (!args) return null;
  const { x, y, axis } = args as { x?: unknown; y?: unknown; axis?: unknown };
  if (!Number.isInteger(x) || !Number.isInteger(y)) return null;
  if (typeof axis !== 'string') return null;
  if (!INTERCHANGE_AXES.includes(axis as 'h' | 'v')) return null;
  return { x: x as number, y: y as number, axis: axis as 'h' | 'v' };
}

/** What a command did when it was applied. */
export interface CommandOutcome {
  command: Command;
  /**
   * True when the verb changed something.
   *
   * A `PAVE` on wet concrete is well-formed, correctly ordered, and does
   * nothing — the cell is curing. The distinction matters to a client that
   * counts the ground it has broken, and to anyone reading a replay wondering
   * why the lot did not grow.
   */
  applied: boolean;
}

/** Anything that accepts scheduled commands. Today a `SurfaceSim`. */
export interface CommandTarget {
  /** The earliest tick a new command may be stamped with. */
  earliestSchedulableTick(): number;
  schedule(command: Command): ScheduleResult;
}

/**
 * Stamps and numbers one actor's commands.
 *
 * The client side of the scheme: it owns the `seq` counter that keeps one
 * actor's commands in the order they were issued, and it picks the tick to aim
 * at. Nothing here is part of the deterministic core — which tick a command
 * lands on is a scheduling decision, and `docs/determinism.md` rule 6 exists so
 * that the decision cannot change what the command *does*.
 *
 * `delayTicks` is zero in single-player, where the earliest schedulable tick is
 * the honest target: there is no network, so there is nothing to be early for.
 * When a server exists this is where `InputDelayEstimator.delayTicks` goes.
 */
export class CommandIssuer {
  private seq = 0;

  constructor(
    private readonly target: CommandTarget,
    readonly actor: string = LOCAL_MAYOR,
    private delayTicks = 0
  ) {}

  /** Commands issued so far. The next one's `seq`. */
  get issued(): number {
    return this.seq;
  }

  /** How many ticks ahead of the earliest this issuer aims. */
  setDelayTicks(ticks: number): void {
    this.delayTicks = Math.max(0, Math.floor(ticks));
  }

  issue(verb: string, args?: Record<string, unknown>): ScheduleResult {
    const command: Command = {
      tick: this.target.earliestSchedulableTick() + this.delayTicks,
      actor: this.actor,
      seq: this.seq++,
      verb,
      args,
    };
    return this.target.schedule(command);
  }

  /** Shorthand for the mayor's verb. */
  pave(x: number, y: number): ScheduleResult {
    return this.issue(MayorVerb.PAVE, { x, y });
  }

  /** Shorthand for the mayor's second verb: the strike, at one facility. */
  withdrawStaffing(facility: number): ScheduleResult {
    return this.issue(MayorVerb.WITHDRAW_STAFFING, { facility });
  }

  /** Shorthand for the mayor's third: back to work, everywhere. */
  restoreStaffing(): ScheduleResult {
    return this.issue(MayorVerb.RESTORE_STAFFING);
  }

  /** Shorthand for the first of §10's mercy builds: deed one cell wild. */
  covenant(x: number, y: number): ScheduleResult {
    return this.issue(MayorVerb.COVENANT, { x, y });
  }

  /** Shorthand for the second: plant a bush that will come in later. */
  hedgerow(x: number, y: number): ScheduleResult {
    return this.issue(MayorVerb.HEDGEROW, { x, y });
  }

  /** Shorthand for the third: a way under one paved cell. */
  undercross(x: number, y: number): ScheduleResult {
    return this.issue(MayorVerb.UNDERCROSS, { x, y });
  }

  /** Shorthand for the mayor's seventh: where national freight enters the map. */
  interchange(x: number, y: number, axis: 'h' | 'v' = 'h'): ScheduleResult {
    return this.issue(MayorVerb.INTERCHANGE, { x, y, axis });
  }

  /**
   * Shorthand for the governor's first.
   *
   * On the same issuer class rather than on a `GovernorIssuer` of its own,
   * because stamping and numbering a command is identical work whoever is doing
   * it — what distinguishes the two is the `actor` an issuer was constructed
   * with, which is the only thing the queue's total order cares about.
   */
  forcePlace(facility: FacilityTypeName, x: number, y: number): ScheduleResult {
    return this.issue(GovernorVerb.FORCE_PLACE, { facility, x, y });
  }

  /** Shorthand for the governor's second: the extraction dial. Negative is a grant. */
  setTithe(rate: number): ScheduleResult {
    return this.issue(GovernorVerb.SET_TITHE, { rate });
  }
}

/** Build an `invalid` rejection. Only a simulation knows what a verb means. */
export function invalidCommand(detail: string): ScheduleRejection {
  return { ok: false, reason: 'invalid', detail };
}
